import type { Messages } from './messages';

/**
 * Copy for `src/app/tenant/models/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `tenantModels.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 */
export const tenantModels = {
  /* ---- §11 الأسعار --------------------------------------------------- */
  'tenantModels.priceTitle': 'الأسعار',
  'tenantModels.priceHint':
    'ما تُحاسَب عليه هذه المنشأة لكل مليون وحدة، بالريال. النموذج بلا سعر لا يُضاف إلى أي حدّ — وهذا ما يعنيه «لم يُخبرنا أحد بالسعر»، لا أنه مجاني.',
  'tenantModels.priceUnpriced': 'لم يُسعَّر {count} من النماذج. إجاباتها لا تُحتسب في أي حدّ ولا تظهر في لوحة الاستهلاك.',
  'tenantModels.priceUnpricedShort': 'نماذج بلا سعر',
  'tenantModels.priceUnset': 'بلا سعر',
  'tenantModels.priceRate': 'دخل {input} · خرج {output} ر.س / مليون',
  'tenantModels.priceEdit': 'تعديل السعر',
  'tenantModels.priceInput': 'سعر الدخل',
  'tenantModels.priceOutput': 'سعر الخرج',
  'tenantModels.priceCacheRead': 'قراءة من الكاش',
  'tenantModels.priceCacheReadHint': 'اتركه فارغًا إن لم يصلك السعر؛ عندها يُحاسَب بسعر الدخل، وهو تقدير أعلى لا أقل.',
  'tenantModels.priceCacheWrite': 'كتابة إلى الكاش',
  'tenantModels.priceCacheWriteHint': 'لا تُحاسِب OpenAI عليها؛ تخزينها تلقائي. اتركه فارغًا لنماذجها.',
  'tenantModels.priceApplyPublished': 'عبّئ السعر المعلَن ({readAt})',
  'tenantModels.pricePublished': 'المعلَن: دخل ${input} · خرج ${output} لكل مليون، بسعر تحويل {rate}.',
  'tenantModels.priceError.notANumber': 'أدخل رقمًا.',
  'tenantModels.priceError.negative': 'لا يصحّ سعر بالسالب.',
  'tenantModels.priceError.halfPriced': 'أدخل سعري الدخل والخرج معًا — نموذج مُسعَّر على نصفه يحاسِب على نصف ما يكلّف.',

  'tenantModels.title': 'النماذج',
  'tenantModels.stale': 'تعذّر تحديث القائمة؛ المعروض آخر ما حُمِّل.',
  'tenantModels.race': 'تغيّر النموذج الافتراضي في الوقت نفسه بطلب آخر، فلم يُطبَّق تغييرك. أُعيد تحميل القائمة؛ راجعها ثم أعد المحاولة.',

  /* Sections */
  'tenantModels.sections': 'أقسام النماذج',
  'tenantModels.sectionSummary': 'الملخص والإنفاق',
  'tenantModels.sectionFailed': 'فيه تغيير لم يُحفظ',

  /* Summary and spend */
  'tenantModels.summaryTitle': 'النماذج والإنفاق',
  'tenantModels.enabledCount': 'النماذج المفعّلة',
  'tenantModels.enabledOf': '{enabled} من {total}',
  'tenantModels.default': 'النموذج الافتراضي',
  'tenantModels.defaultHint': 'ما يجيب به النظام حين لا يختار العضو نموذجًا. تختاره إدارة المنشأة.',
  'tenantModels.noDefault': 'لا نموذج افتراضيًا بعد',
  'tenantModels.noDefaultHint': 'أول نموذج تفعّله يصبح الافتراضي لهذه المنشأة.',
  'tenantModels.spendMeter': 'الإنفاق هذا الشهر مقابل سقف المنشأة',
  'tenantModels.spendDescription': 'الشهر بتوقيت الرياض، كما يُحتسب للفوترة.',

  /* The list */
  'tenantModels.readOnly': 'رتبتك تتيح عرض النماذج دون تغييرها؛ التفعيل والتعطيل يحتاجان صلاحية إدارة المنشآت.',
  'tenantModels.providerCount': 'مفعّل {enabled} من {total}',
  'tenantModels.tableCaption': 'نماذج {provider} لهذه المنشأة',
  'tenantModels.colModel': 'النموذج',
  'tenantModels.colQuality': 'الجودة',
  'tenantModels.colState': 'متاح للمنشأة',
  'tenantModels.colCost': 'الكلفة النسبية',
  'tenantModels.colContext': 'نافذة السياق',
  'tenantModels.contextTokens': '{count} رمز',
  'tenantModels.defaultBadge': 'افتراضي',
  'tenantModels.switchLabel': 'إتاحة {name} لأعضاء المنشأة',
  'tenantModels.defaultLocked': 'لا يُعطَّل من هنا: على مشرفي المنشأة نقل الافتراضي إلى نموذج آخر أولًا.',
  'tenantModels.rowFailed': 'لم يُحفظ: {message}',
  'tenantModels.emptyTitle': 'لا نماذج في الفهرس',
  'tenantModels.emptyBody': 'فهرس النماذج على مستوى المنصّة فارغ، فلا شيء يُتاح لهذه المنشأة قبل أن يُضاف إليه.',
  'tenantModels.emptyAction': 'فهرس النماذج',

  /* Disabling */
  'tenantModels.confirmTitle': 'تعطيل {name} لهذه المنشأة؟',
  'tenantModels.confirmConsequence':
    'لن يظهر هذا النموذج لأعضاء المنشأة بعد الآن. من تعمل محادثته عليه تنتقل إلى {fallback} عند سؤاله التالي، ولا تُعاد كتابة المحادثات السابقة. يمكن إعادة تفعيله في أي وقت.',
  'tenantModels.confirmFallback': 'النموذج الافتراضي',
  'tenantModels.confirmAffected': 'يبقى متاحًا بعده: {count}',
  'tenantModels.confirmLabel': 'تعطيل النموذج',

  /* Answer sources — what a reply may be made of, beside which models may write it */
  'tenantModels.sources': 'مصادر الإجابة',
  'tenantModels.sourcesHint':
    'مستندات المنشأة هي المصدر دائمًا، ويُقرأ أوّلًا في كل سؤال. هذان الخياران يقرّران ما يفعله المساعد حين لا تجيب المستندات.',
  'tenantModels.general': 'الإجابة من المعرفة العامة',
  'tenantModels.generalHint':
    'حين لا تجيب مستندات المنشأة، يجيب المساعد ممّا تعلّمه حتى تاريخ تدريبه ويقول إن الإجابة عامة. مطفأً، يعتذر بدل أن يجيب. لا يخرج السؤال من المنصّة في الحالتين.',
  'tenantModels.webSearch': 'البحث في الإنترنت',
  'tenantModels.webSearchHint':
    'حين لا تجيب المستندات، يبحث المساعد في الإنترنت ويعرض ما وجده منفصلًا، ولا يُعدّه مصدرًا من مصادر المنشأة. **يخرج نصّ السؤال إلى محرّك بحث** — قرار سرّية لا قرار جودة.',
  'tenantModels.webSearchNeedsGeneral':
    'يعمل البحث مع المعرفة العامة أو بدونها؛ وفي الحالتين تُقرأ مستندات المنشأة أوّلًا.',
  'tenantModels.sourceSaved': 'حُفِظ.',
  'tenantModels.sourceFailed': 'لم يُحفظ: {message}',

  /* Results */
  'tenantModels.enabled': 'أُتيح {name} لأعضاء المنشأة.',
  'tenantModels.enabledAsDefault': 'أُتيح {name} وأصبح النموذج الافتراضي للمنشأة.',
  'tenantModels.disabled': 'عُطّل {name} لهذه المنشأة.',
  'tenantModels.unchanged': '{name} كان على هذه الحالة أصلًا؛ لم يتغيّر شيء.',
} as const satisfies Messages<'tenantModels'>;
